/**
 * Cards Page — list saved payment cards, add/remove cards.
 * Uses Payme Subscribe API for card tokenization via Edge Function.
 */

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, CreditCard, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@turnifly/shared/ui'
import { Badge } from '@turnifly/shared/ui'
import { EmptyState } from '@turnifly/shared/ui'
import { useUserCards, useRemoveCard } from '@turnifly/shared/hooks'
import { triggerHapticImpact as hapticImpact, triggerHapticNotification as hapticNotification } from '@turnifly/shared/utils'
import { CardAddSheet } from './card-add-sheet'

interface CardsPageProps {
  isOpen: boolean
  onClose: () => void
}

export const CardsPage: React.FC<CardsPageProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { data: cards, isLoading } = useUserCards()
  const removeCardMutation = useRemoveCard()
  const [showAddSheet, setShowAddSheet] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const handleDelete = (cardId: string): void => {
    hapticImpact('medium')
    setDeleteConfirmId(cardId)
  }

  const confirmDelete = (): void => {
    if (!deleteConfirmId) return
    removeCardMutation.mutate(deleteConfirmId, {
      onSuccess: () => {
        hapticNotification('success')
        setDeleteConfirmId(null)
      },
    })
  }

  const cardBrandIcon = (brand: string | null): string => {
    switch (brand) {
      case 'uzcard': return '💳'
      case 'humo': return '🏦'
      case 'visa': return '💎'
      case 'mastercard': return '🔶'
      default: return '💳'
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          <motion.div
            initial={{ y: '100%', opacity: 0.8 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.8 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-50 bg-bg flex flex-col inset-x-0 bottom-0 rounded-t-[32px] max-h-[92vh]
              lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2
              lg:rounded-3xl lg:max-h-[85vh] lg:w-full lg:max-w-md
              lg:border lg:border-white/[0.06] lg:shadow-glass"
          >
            {/* Header — safe area */}
            <div className="flex items-center justify-between px-5 pb-2 shrink-0" style={{ paddingTop: 'max(20px, calc(var(--tg-safe-area-inset-top) + 8px))' }}>
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/[0.04]"
              >
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">{t('cards.title')}</h2>
              <div className="w-10" />
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-8">
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                </div>
              ) : !cards || cards.length === 0 ? (
                <EmptyState
                  icon={<CreditCard size={40} className="text-text-hint/40" />}
                  title={t('cards.noCards')}
                  description={t('cards.noCardsDesc')}
                />
              ) : (
                <div className="space-y-3 mt-2">
                  {cards.map((card) => (
                    <div
                      key={card.id}
                      className="flex items-center gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06]"
                    >
                      <span className="text-2xl">{cardBrandIcon(card.card_brand)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text truncate">
                          {card.card_number_masked}
                        </p>
                        <p className="text-xs text-text-hint capitalize">
                          {card.card_brand ?? card.provider}
                        </p>
                      </div>
                      {card.is_default && (
                        <Badge variant="primary" className="text-[10px] shrink-0">
                          {t('cards.default')}
                        </Badge>
                      )}
                      <button
                        onClick={() => handleDelete(card.id)}
                        className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/[0.04] shrink-0"
                      >
                        <Trash2 size={16} className="text-text-hint" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Card Button */}
            <div className="px-5 pt-2 shrink-0" style={{ paddingBottom: 'max(24px, calc(var(--tg-safe-area-inset-bottom) + 12px))' }}>
              <Button
                variant={cards && cards.length > 0 ? 'secondary' : 'primary'}
                fullWidth
                onClick={() => {
                  hapticImpact('light')
                  setShowAddSheet(true)
                }}
              >
                <Plus size={18} className="mr-2" />
                {t('cards.addCard')}
              </Button>
            </div>
          </motion.div>

          {/* Card Add Sheet */}
          <CardAddSheet
            isOpen={showAddSheet}
            onClose={() => setShowAddSheet(false)}
          />

          {/* Delete Confirmation */}
          <AnimatePresence>
            {deleteConfirmId && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setDeleteConfirmId(null)}
                  className="fixed inset-0 bg-black/60 z-[60]"
                />
                <motion.div
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: 50, opacity: 0 }}
                  className="fixed z-[60] inset-x-4 bottom-8 bg-surface rounded-3xl p-6 border border-white/[0.06]
                    lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:w-full lg:max-w-sm"
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="w-14 h-14 rounded-2xl bg-danger/10 flex items-center justify-center mb-4">
                      <Trash2 size={24} className="text-danger" />
                    </div>
                    <h3 className="text-lg font-display font-bold text-text mb-2">
                      {t('cards.deleteCard')}
                    </h3>
                    <p className="text-sm text-text-hint mb-6">
                      {t('cards.deleteCardDesc')}
                    </p>
                    <div className="flex gap-3 w-full">
                      <Button
                        variant="secondary"
                        fullWidth
                        onClick={() => setDeleteConfirmId(null)}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        variant="danger"
                        fullWidth
                        onClick={confirmDelete}
                        isLoading={removeCardMutation.isPending}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  )
}
